import React, { useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faBolt,
    faTools,
    faPlug,
    faIndustry,
    faCamera,
    faLightbulb,
    faWrench,
    faExclamationTriangle,
    faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import ElectricalSafetyDashboard from './ElectricalSafetyDashboard';
import CircuitBreakerChecks from './CircuitBreakerChecks/CircuitBreakerChecks';
import ElectricalEquipmentLogs from './ElectricalEquipmentLogs/ElectricalEquipmentLogs';
import EmergencyPowerSystems from './EmergencyPowerSystems/EmergencyPowerSystems';
import FuseBoardInspections from './FuseBoardInspections/FuseBoardInspections';
import GroundingAndBonding from './GroundingAndBonding/GroundingAndBonding';
import LoadTesting from './LoadTesting/LoadTesting';
import PATTestingLogs from './PATTestingLogs/PATTestingLogs';
import RCDTesting from './RCDTesting/RCDTesting';
import SwitchgearInspections from './SwitchgearInspections/SwitchgearInspections';
import ThermalImagingReports from './ThermalImagingReports/ThermalImagingReports';
import ComingSoonModal from '../../pages/ComingSoon/ComingSoonModal';
import './ElectricalSafety.css';

const SHOWCASE_MODE = false;
const COMING_SOON_SECTIONS = [
  'circuit-breakers', 'equipment-logs', 'emergency-power', 'fuse-boards',
  'grounding', 'load-testing', 'pat-testing', 'rcd-testing', 'switchgear', 'thermal-imaging'
];

const sidebarSections = [
  { id: 'dashboard',        title: 'Dashboard',           icon: faChartLine,            component: ElectricalSafetyDashboard },
  { id: 'circuit-breakers', title: 'Circuit Breakers',    icon: faBolt,                 component: CircuitBreakerChecks },
  { id: 'equipment-logs',   title: 'Equipment Logs',      icon: faTools,                component: ElectricalEquipmentLogs },
  { id: 'emergency-power',  title: 'Emergency Power',     icon: faPlug,                 component: EmergencyPowerSystems },
  { id: 'fuse-boards',      title: 'Fuse Boards',         icon: faIndustry,             component: FuseBoardInspections },
  { id: 'grounding',        title: 'Grounding & Bonding', icon: faLightbulb,            component: GroundingAndBonding },
  { id: 'load-testing',     title: 'Load Testing',        icon: faWrench,               component: LoadTesting },
  { id: 'pat-testing',      title: 'PAT Testing',         icon: faExclamationTriangle,  component: PATTestingLogs },
  { id: 'rcd-testing',      title: 'RCD Testing',         icon: faFileAlt,              component: RCDTesting },
  { id: 'switchgear',       title: 'Switchgear',          icon: faIndustry,             component: SwitchgearInspections },
  { id: 'thermal-imaging',  title: 'Thermal Imaging',     icon: faCamera,               component: ThermalImagingReports },
];

const SUB_NAV_ITEMS = sidebarSections.map(s => ({
  id: s.id,
  label: s.title,
  icon: <FontAwesomeIcon icon={s.icon} />
}));

const ElectricalSafety = () => {
  const { id } = useParams();
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonFeatureName, setComingSoonFeatureName] = useState('');

  const ctx = useOutletContext() || {};
  const activeSubSection = ctx.activeSubSection || 'dashboard';
  const setActiveSubSection = ctx.setActiveSubSection || (() => {});

  const handleSectionClick = (section) => {
    if (SHOWCASE_MODE && COMING_SOON_SECTIONS.includes(section)) {
      const sectionData = sidebarSections.find(s => s.id === section);
      setComingSoonFeatureName(sectionData ? sectionData.title : 'This Feature');
      setShowComingSoonModal(true);
      return;
    }
    setActiveSubSection(section);
  };

  const renderContent = () => {
    const section = sidebarSections.find(s => s.id === activeSubSection);
    if (!section?.component) {
      const C = sidebarSections[0].component;
      return <C propertyId={id} onNavigate={handleSectionClick} />;
    }
    if (activeSubSection === 'dashboard') {
      const Component = section.component;
      return <Component propertyId={id} onNavigate={handleSectionClick} />;
    }
    const Component = section.component;
    return <Component propertyId={id} />;
  };

  const visiblePills = activeSubSection === 'dashboard'
    ? SUB_NAV_ITEMS.filter(i => i.id === 'dashboard')
    : SUB_NAV_ITEMS;

  const pillNav = (
    <nav className="rs-subnav">
      {visiblePills.map((item) => (
        <button
          key={item.id}
          className={`rs-pill${activeSubSection === item.id ? ' rs-pill-active' : ''}`}
          onClick={() => handleSectionClick(item.id)}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );

  return (
    <div className="electrical-safety-container">
      <div className="rs-subnav-mobile-wrap">{pillNav}</div>
      <div className="electrical-safety-content">
        <div className="electrical-safety-main">
          <div className="rs-subnav-desktop-wrap">{pillNav}</div>
          {renderContent()}
        </div>
      </div>
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        featureName={comingSoonFeatureName}
      />
    </div>
  );
};

export default ElectricalSafety;
